import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Settings, Upload, Image as ImageIcon, Save } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { cargarBackupELotes } from "../importLotesToFirestore";
import { cargarBackupEMovimientos } from "../importMovimientosToFirestore";
import { cargarBackupEVendedores } from "../importVendedoresToFirestore";
import { toast } from 'sonner';
import { BackupManager } from './BackupManager';

export default function ConfiguracionProyecto() {
  const { config, setConfig } = useAppData();
  const [migrationStatus, setMigrationStatus] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialConfig, setInitialConfig] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);

  // Guardar configuración inicial al cargar (solo una vez)
  useEffect(() => {
    if (config && initialConfig === '') {
      const configString = JSON.stringify(config);
      setInitialConfig(configString);
    }
  }, [config, initialConfig]);

  // Detectar cambios comparando con configuración inicial
  useEffect(() => {
    if (initialConfig !== '' && config) {
      const currentConfig = JSON.stringify(config);
      setHasChanges(currentConfig !== initialConfig);
    }
  }, [config, initialConfig]);

  const handleChange = (field: string, value: any) => {
    setConfig((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const { db } = await import('../firebaseConfig');
      const { doc, setDoc } = await import('firebase/firestore');

      // Sanitize config to remove undefined values which Firestore doesn't support
      const sanitizedConfig = JSON.parse(JSON.stringify(config));
      Object.keys(sanitizedConfig).forEach(key => {
        if (sanitizedConfig[key] === undefined) {
          delete sanitizedConfig[key];
        }
      });

      await setDoc(doc(db, 'config', 'proyecto'), sanitizedConfig);

      const newConfigString = JSON.stringify(config);
      setInitialConfig(newConfigString);
      setHasChanges(false);

      toast.success('Configuración guardada', {
        description: 'Los cambios se han aplicado correctamente',
        className: 'bg-card border-border text-foreground',
      });
    } catch (error: any) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar', {
        description: error.message || 'Error desconocido al escribir en Firestore',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Firestore limit is 1MB. Base64 adds ~33%. So we limit to ~700KB. Safe bet: 500KB.
    if (!file.type.startsWith('image/') || file.size > 500 * 1024) {
      toast.error('Archivo inválido', { description: 'Debe ser imagen y menor a 500KB para guardarse en la base de datos.' });
      return;
    }

    try {
      setUploadingLogo(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        handleChange('logoUrl', base64String);
        setUploadingLogo(false);
        toast.success('Logo cargado');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error al cargar logo:', error);
      setUploadingLogo(false);
    }
  };

  const valorTotal = config.lotes * config.precioBase;
  const capacidad = config.filas * config.columnas;

  return (
    <div className="max-w-4xl mx-auto p-6 pb-20">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-semibold text-foreground tracking-tight">Configuración</h1>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={`rounded-[6px] px-4 py-2 font-medium transition-all ${hasChanges
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
        >
          {isSaving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      {/* General */}
      <SectionHeader title="General" />
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <SettingRow label="Nombre del Proyecto" description="Nombre visible en la aplicación">
          <Input
            value={config.nombre}
            onChange={e => handleChange('nombre', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Ubicación" description="Ubicación geográfica del proyecto">
          <Input
            value={config.ubicacion}
            onChange={e => handleChange('ubicacion', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Fecha de Inicio" description="Fecha de lanzamiento del proyecto">
          <Input
            type="date"
            value={config.fecha}
            onChange={e => handleChange('fecha', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm [color-scheme:light] dark:[color-scheme:dark]"
          />
        </SettingRow>
        <SettingRow label="Moneda" description="Moneda principal para transacciones">
          <Select value={config.moneda} onValueChange={v => handleChange('moneda', v)}>
            <SelectTrigger className="bg-secondary border-input rounded-[6px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="GTQ">Quetzal (GTQ)</SelectItem>
              <SelectItem value="USD">Dólar (USD)</SelectItem>
            </SelectContent>
          </Select>
        </SettingRow>
      </div>

      {/* Empresa */}
      <SectionHeader title="Empresa" />
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <SettingRow label="Logo de la Empresa" description="Aparecerá en los recibos generados">
          <div className="flex items-center gap-4 justify-end">
            {config.logoUrl && (
              <img src={config.logoUrl} alt="Logo" className="h-8 w-8 object-contain rounded bg-muted p-0.5" />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
              className="hidden"
              id="logo-upload"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('logo-upload')?.click()}
              disabled={uploadingLogo}
              className="bg-secondary border-input hover:bg-accent rounded-[6px] h-9 text-xs"
            >
              {uploadingLogo ? 'Subiendo...' : 'Cambiar Logo'}
            </Button>
          </div>
        </SettingRow>
        <SettingRow label="Razón Social" description="Nombre legal de la empresa">
          <Input
            value={config.razonSocial || ''}
            onChange={e => handleChange('razonSocial', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="NIT" description="Número de Identificación Tributaria">
          <Input
            value={config.nit || ''}
            onChange={e => handleChange('nit', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Dirección" description="Dirección fiscal completa">
          <Input
            value={config.direccionCompleta || ''}
            onChange={e => handleChange('direccionCompleta', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Teléfono" description="Número de contacto principal">
          <Input
            value={config.telefono1 || ''}
            onChange={e => handleChange('telefono1', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Email" description="Correo electrónico de contacto">
          <Input
            value={config.email || ''}
            onChange={e => handleChange('email', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
      </div>

      {/* Vendedores */}
      <SectionHeader title="Vendedores" />
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <SettingRow label="Tipo de Comisión" description="Cómo se calculará la comisión">
          <Select
            value={config.tipoComision || 'porcentaje'}
            onValueChange={v => handleChange('tipoComision', v)}
          >
            <SelectTrigger className="bg-secondary border-input rounded-[6px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
              <SelectItem value="fijo">Monto Fijo (Q)</SelectItem>
            </SelectContent>
          </Select>
        </SettingRow>

        {(config.tipoComision === 'porcentaje' || !config.tipoComision) && (
          <SettingRow label="Porcentaje de Comisión" description="Porcentaje sobre el valor total de la venta">
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={config.porcentajeComision || ''}
                onChange={e => handleChange('porcentajeComision', Number(e.target.value))}
                className="bg-secondary border-input rounded-[6px] h-9 text-sm pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
          </SettingRow>
        )}

        {config.tipoComision === 'fijo' && (
          <SettingRow label="Monto Fijo por Lote" description="Monto exacto en Quetzales por cada lote vendido">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">Q.</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={config.montoComisionFijo || ''}
                onChange={e => handleChange('montoComisionFijo', Number(e.target.value))}
                className="bg-secondary border-input rounded-[6px] h-9 text-sm pl-8"
              />
            </div>
          </SettingRow>
        )}
      </div>

      {/* Bancaria */}
      <SectionHeader title="Información Bancaria" />
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <SettingRow label="Banco" description="Institución financiera">
          <Input
            value={config.nombreBanco || ''}
            onChange={e => handleChange('nombreBanco', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Número de Cuenta" description="Cuenta para depósitos">
          <Input
            value={config.numeroCuenta || ''}
            onChange={e => handleChange('numeroCuenta', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Tipo de Cuenta" description="Monetaria o Ahorro">
          <Input
            value={config.tipoCuenta || ''}
            onChange={e => handleChange('tipoCuenta', e.target.value)}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
      </div>

      {/* Lotes */}
      <SectionHeader title="Configuración de Lotes" />
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <SettingRow label="Total de Lotes" description="Cantidad total de lotes en el proyecto">
          <Input
            type="number"
            value={config.lotes}
            onChange={e => handleChange('lotes', Number(e.target.value))}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Precio Base" description="Precio estándar por lote">
          <Input
            type="number"
            value={config.precioBase}
            onChange={e => handleChange('precioBase', Number(e.target.value))}
            className="bg-secondary border-input rounded-[6px] h-9 text-sm"
          />
        </SettingRow>
        <SettingRow label="Dimensiones del Croquis" description="Filas x Columnas para la visualización">
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Filas"
              value={config.filas}
              onChange={e => handleChange('filas', Number(e.target.value))}
              className="bg-secondary border-input rounded-[6px] h-9 text-sm flex-1"
            />
            <span className="text-muted-foreground self-center">x</span>
            <Input
              type="number"
              placeholder="Cols"
              value={config.columnas}
              onChange={e => handleChange('columnas', Number(e.target.value))}
              className="bg-secondary border-input rounded-[6px] h-9 text-sm flex-1"
            />
          </div>
        </SettingRow>
        <div className="p-4 bg-muted/20 border-t border-border">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Capacidad: {capacidad} lotes</span>
            <span>Valor Total: Q {valorTotal.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Backup */}
      <SectionHeader title="Sistema" />
      <div className="bg-card border border-border rounded-lg overflow-hidden p-4">
        <BackupManager />
      </div>

    </div >
  );
}

const SettingRow = ({ label, description, children, className = "" }: { label: string, description?: string, children: React.ReactNode, className?: string }) => (
  <div className={`flex flex-col md:flex-row md:items-center justify-between p-4 border-b border-border last:border-0 gap-4 ${className}`}>
    <div className="flex-1">
      <div className="text-sm font-medium text-foreground">{label}</div>
      {description && <div className="text-xs text-muted-foreground mt-0.5">{description}</div>}
    </div>
    <div className="w-full md:w-[300px]">
      {children}
    </div>
  </div>
);

const SectionHeader = ({ title }: { title: string }) => (
  <h2 className="text-lg font-medium text-foreground mt-8 mb-4 px-1">{title}</h2>
); 